import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../store'
import WebSocketManager from '../../services/websocketManager'
import { setLoadingMoreMessages } from '../slices/negotiationWebsocketSlice'

const wsManager = WebSocketManager.getInstance()

export const sendNegotiationInvite = createAsyncThunk<
  void,
  { inviteeEmail: string; roomId: string; phaseId?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/sendNegotiationInvite',
  async ({ inviteeEmail, roomId, phaseId }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('negotiation', {
      type: 'INVITE_NEGOTIATION',
      invitee: inviteeEmail,
      room_id: roomId,
      phaseId: phaseId,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const createGroupChat = createAsyncThunk<
  void,
  { inviteeEmails: string[]; phaseId?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/createGroupChat',
  async ({ inviteeEmails, phaseId }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('negotiation', {
      type: 'CREATE_GROUP_CHAT',
      invitees: inviteeEmails,
      phase_id: phaseId,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const addGroupMembers = createAsyncThunk<
  void,
  { negotiationId: string; inviteeEmails: string[] },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/addGroupMembers',
  async ({ negotiationId, inviteeEmails }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('negotiation', {
      type: 'ADD_GROUP_MEMBERS',
      negotiation_id: negotiationId,
      invitees: inviteeEmails,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const respondToNegotiationInvite = createAsyncThunk<
  void,
  { negotiationId: string; accepted: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/respondToNegotiationInvite',
  async ({ negotiationId, accepted }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('negotiation', {
      type: accepted ? 'NEGOTIATION_ACCEPTED' : 'NEGOTIATION_REJECTED',
      negotiation_id: negotiationId,
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

// Negotiation websocket thunks
export const connectNegotiationWebSocket = createAsyncThunk<
  void,
  {
    jwtToken: string
    roomId: string
    negotiationId?: string
    phaseId?: string
    roundId?: string
    impersonatePlayerId?: string
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/connectNegotiation',
  async (
    { jwtToken, roomId, negotiationId, phaseId, roundId, impersonatePlayerId },
    { rejectWithValue }
  ) => {
    try {
      return await wsManager.connectToNegotiation(
        jwtToken,
        roomId,
        negotiationId,
        phaseId,
        roundId,
        impersonatePlayerId
      )
    } catch (error) {
      return rejectWithValue(
        (error as Error).message || 'Failed to connect to negotiation websocket'
      )
    }
  }
)

export const disconnectNegotiationWebSocket = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>('websocket/disconnectNegotiation', async () => {
  return await wsManager.disconnect('negotiation')
})

export const sendNegotiationMessage = createAsyncThunk<
  void,
  { message: string; negotiationId: string; replyTo?: string | number },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/sendNegotiationMessage',
  async ({ message, negotiationId, replyTo }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('negotiation', {
      type: 'SEND_MESSAGE',
      message,
      negotiation_id: negotiationId,
      reply_to: replyTo,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const terminateNegotiation = createAsyncThunk<
  void,
  { negotiationId: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/respondToNegotiationInvite',
  async ({ negotiationId }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('negotiation', {
      type: 'TERMINATE_NEGOTIATION',
      negotiation_id: negotiationId,
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const loadMoreMessages = createAsyncThunk<
  void,
  { page: number; pageSize?: number; negotiationId?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/loadMoreMessages',
  async (
    { page, pageSize = 20, negotiationId },
    { dispatch, rejectWithValue }
  ) => {
    dispatch(setLoadingMoreMessages(true))

    const success = wsManager.sendMessage('negotiation', {
      type: 'LOAD_MORE_MESSAGES',
      page,
      page_size: pageSize,
      negotiation_id: negotiationId,
    })

    if (!success) {
      dispatch(setLoadingMoreMessages(false))
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const loadNegotiationMessages = createAsyncThunk<
  void,
  { negotiationId: string; page: number; pageSize?: number },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/loadNegotiationMessages',
  async (
    { negotiationId, page, pageSize = 20 },
    { dispatch, rejectWithValue }
  ) => {
    dispatch(setLoadingMoreMessages(true))

    const success = wsManager.sendMessage('negotiation', {
      type: 'LOAD_NEGOTIATION_MESSAGES',
      negotiation_id: negotiationId,
      page,
      page_size: pageSize,
    })

    if (!success) {
      dispatch(setLoadingMoreMessages(false))
      return rejectWithValue('WebSocket is not connected')
    }
  }
)
